import React, { useRef, useState } from "react";
import { api, BACKEND_URL } from "../../lib/api";
import { toast } from "sonner";
import { Upload, X, AlertCircle, Film, Loader2 } from "lucide-react";

const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const VIDEO_TYPES = ["video/mp4", "video/quicktime", "video/webm", "video/x-m4v"];
const IMAGE_MAX = 8 * 1024 * 1024;
const VIDEO_MAX = 150 * 1024 * 1024;
const KIND_LABELS = { product: "Product", charger: "Charger", reference: "Style ref", reference_frame: "Video frame" };
const errText = (e, fallback) => {
  if (!e?.response) return `${fallback}: network error — check your connection and retry.`;
  if (e.response.status === 401) return "Your session expired. Log in again — your draft is saved.";
  return e.response.data?.detail || `${fallback} (HTTP ${e.response.status})`;
};

async function uploadVideo(file, onProgress) {
  const { data: init } = await api.post("/studio/wizard/video/init", { filename: file.name, size: file.size });
  const size = init.chunk_size;
  const total = Math.ceil(file.size / size);
  for (let i = 0; i < total; i++) {
    const blob = file.slice(i * size, Math.min(file.size, (i + 1) * size));
    let tries = 0;
    for (;;) {
      try {
        await api.put(`/studio/wizard/video/${init.upload_id}/chunk?index=${i}`, blob, {
          headers: { "Content-Type": "application/octet-stream" },
        });
        break;
      } catch (e) {
        if (++tries >= 3 || e?.response?.status === 401) throw e;
        await new Promise((r) => setTimeout(r, 800 * tries));
      }
    }
    onProgress(Math.round(((i + 1) / total) * 90));
  }
  const { data } = await api.post(`/studio/wizard/video/${init.upload_id}/complete`, { frames: 8 });
  onProgress(100);
  return data;
}

function Tile({ a, onRemove, onKind }) {
  return (
    <div data-testid={`attachment-${a.id}`} className="rounded-md border border-slate-200 overflow-hidden bg-white">
      <div className="relative aspect-square">
        <img src={`${BACKEND_URL}${a.url}`} alt={a.name} className="w-full h-full object-cover" />
        <button
          type="button"
          data-testid={`remove-attachment-${a.id}`}
          aria-label={`Remove ${a.name}`}
          onClick={() => onRemove(a.id)}
          className="absolute top-1 right-1 w-6 h-6 rounded-full bg-slate-900/75 text-white flex items-center justify-center hover:bg-slate-900"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      {a.kind === "reference_frame" ? (
        <div className="text-[10px] font-mono px-1.5 py-1 text-slate-500 truncate">Frame {a.t_sec != null ? `@ ${a.t_sec}s` : ""}</div>
      ) : (
        <select
          data-testid={`attachment-kind-${a.id}`}
          value={a.kind || "reference"}
          onChange={(e) => onKind(a.id, e.target.value)}
          className="w-full text-[11px] px-1 py-1 border-t border-slate-200 bg-white"
        >
          <option value="product">Product photo</option>
          <option value="charger">Charger photo</option>
          <option value="reference">Style reference</option>
        </select>
      )}
    </div>
  );
}

export const ReferenceUploader = ({ attachments, onChange }) => {
  const inputRef = useRef(null);
  const [jobs, setJobs] = useState([]); // {key, name, progress, error, kind}
  const [drag, setDrag] = useState(false);
  const busy = jobs.some((j) => !j.error && j.progress < 100);

  const setJob = (key, patch) => setJobs((js) => js.map((j) => (j.key === key ? { ...j, ...patch } : j)));
  const add = (items) => onChange((list) => [...list, ...items]);

  const handleFiles = async (files) => {
    for (const file of files) {
      const key = `${file.name}-${file.size}-${Date.now()}`;
      const isVideo = VIDEO_TYPES.includes(file.type) || /\.(mp4|mov|m4v|webm)$/i.test(file.name);
      const isImage = IMAGE_TYPES.includes(file.type);
      if (!isVideo && !isImage) {
        setJobs((js) => [...js, { key, name: file.name, progress: 0, error: "Unsupported type — use JPG, PNG, WebP or an MP4/MOV video." }]);
        continue;
      }
      if ((isImage && file.size > IMAGE_MAX) || (isVideo && file.size > VIDEO_MAX)) {
        const lim = isImage ? "8 MB per image" : "150 MB per video";
        setJobs((js) => [...js, { key, name: file.name, progress: 0, error: `${(file.size / 1e6).toFixed(1)} MB — limit is ${lim}.` }]);
        continue;
      }
      setJobs((js) => [...js, { key, name: file.name, progress: 1, kind: isVideo ? "video" : "image" }]);
      try {
        if (isVideo) {
          const res = await uploadVideo(file, (p) => setJob(key, { progress: p }));
          add(res.frames);
          toast.success(`${res.frames.length} reference frames extracted from ${file.name} (${res.video.duration_s}s, ${res.video.width}×${res.video.height})`);
        } else {
          const fd = new FormData();
          fd.append("file", file);
          fd.append("kind", "product");
          const { data } = await api.post("/studio/wizard/upload-image", fd, {
            onUploadProgress: (ev) => ev.total && setJob(key, { progress: Math.round((ev.loaded / ev.total) * 99) }),
          });
          add([data]);
        }
        setJob(key, { progress: 100 });
        setTimeout(() => setJobs((js) => js.filter((j) => j.key !== key)), 1500);
      } catch (e) {
        const st = e?.response?.status;
        const retryable = !e?.response || st >= 500 || st === 409;
        setJob(key, { error: errText(e, "Upload failed"), file: retryable ? file : null });
      }
    }
  };

  const pick = () => inputRef.current?.click();
  const retry = (job) => {
    setJobs((js) => js.filter((j) => j.key !== job.key));
    if (job.file) handleFiles([job.file]);
  };
  const remove = (id) => {
    onChange((list) => list.filter((a) => a.id !== id));
    api.delete(`/studio/wizard/attachment/${id}`).catch(() => {});
  };
  const setKind = (id, kind) => {
    onChange((list) => list.map((a) => (a.id === id ? { ...a, kind } : a)));
    api.patch(`/studio/wizard/attachment/${id}`, { kind }).catch(() => toast.error("Could not save photo role"));
  };

  return (
    <div>
      <div
        role="button"
        tabIndex={0}
        data-testid="wiz-file-dropzone"
        aria-label="Upload product photos or a reference video"
        onClick={pick}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), pick())}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); handleFiles(Array.from(e.dataTransfer.files || [])); }}
        className={`mt-1.5 border-2 border-dashed rounded-md p-6 text-center cursor-pointer transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${
          drag ? "border-blue-500 bg-blue-50" : "border-slate-300 hover:border-blue-400 hover:bg-blue-50/30"
        }`}
      >
        {busy ? <Loader2 className="w-6 h-6 mx-auto text-blue-500 mb-2 animate-spin" /> : <Upload className="w-6 h-6 mx-auto text-slate-400 mb-2" />}
        <div className="text-sm text-slate-700 font-medium">Choose files or drop them here</div>
        <div className="text-[11px] text-slate-500 mt-1">
          Product & charger photos (JPG/PNG/WebP · 8 MB) · reference video (MP4/MOV · 150 MB → converted to still frames)
        </div>
      </div>
      <input
        ref={inputRef}
        id="wiz-file"
        data-testid="wiz-file-input"
        type="file"
        accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm,.mp4,.mov"
        multiple
        className="sr-only"
        aria-label="Upload product photos or a reference video"
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          e.target.value = ""; // allow re-selecting the same file after an error
          if (files.length) handleFiles(files);
        }}
      />

      {jobs.length > 0 && (
        <div data-testid="upload-jobs" className="mt-3 space-y-1.5">
          {jobs.map((j) => (
            <div key={j.key} data-testid="upload-job" className="text-xs">
              <div className="flex items-center gap-2">
                {j.kind === "video" ? <Film className="w-3.5 h-3.5 text-slate-500" /> : null}
                <span className="truncate flex-1 text-slate-700">{j.name}</span>
                {j.error ? (
                  <>
                    {j.file && <button type="button" data-testid="upload-retry" onClick={() => retry(j)} className="text-blue-700 font-medium hover:underline">Retry</button>}
                    <button type="button" data-testid="upload-dismiss" aria-label="Dismiss" onClick={() => setJobs((js) => js.filter((x) => x.key !== j.key))} className="text-slate-400 hover:text-slate-700"><X className="w-3.5 h-3.5" /></button>
                  </>
                ) : (
                  <span className="font-mono text-slate-500">{j.progress >= 90 && j.kind === "video" && j.progress < 100 ? "extracting frames…" : `${j.progress}%`}</span>
                )}
              </div>
              {j.error ? (
                <div data-testid="upload-error" className="flex items-start gap-1 text-red-700 mt-0.5"><AlertCircle className="w-3.5 h-3.5 mt-px shrink-0" />{j.error}</div>
              ) : (
                <div className="h-1 bg-slate-100 rounded mt-1 overflow-hidden"><div className="h-full bg-blue-600 transition-[width] duration-300" style={{ width: `${j.progress}%` }} /></div>
              )}
            </div>
          ))}
        </div>
      )}

      {attachments.length > 0 && (
        <>
          <div className="mt-3 grid grid-cols-4 md:grid-cols-6 gap-2">
            {attachments.map((a) => <Tile key={a.id} a={a} onRemove={remove} onKind={setKind} />)}
          </div>
          <div data-testid="attachment-summary" className="mt-2 text-[11px] text-slate-500">
            {Object.entries(KIND_LABELS).map(([k, l]) => {
              const n = attachments.filter((a) => (a.kind || "reference") === k).length;
              return n ? `${l}: ${n}` : null;
            }).filter(Boolean).join(" · ")}
            {" — product & charger photos are shown exactly as uploaded; video frames guide pacing only."}
          </div>
        </>
      )}
    </div>
  );
};
